import React, { type ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';

import { AIMateAnalyticsContext } from '@atlaskit/analytics-namespaced-context';
import { cssMap, cx } from '@atlaskit/css';
import type { OnOpenChangeArgs } from '@atlaskit/dropdown-menu';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import type { AgentVerificationDropdownItemProps } from '@atlaskit/rovo-agent-components/agent-verification-dropdown-item';
import { AgentVerified } from '@atlaskit/rovo-agent-components/agent-verified';
import type { AgentCreatorType } from '@atlaskit/rovo-agent-components/common/types';
import { HiddenIcon } from '@atlaskit/rovo-agent-components/common/ui/HiddenIcon';
import { LinkIconButton } from '@atlaskit/rovo-agent-components/common/ui/LinkIconButton';
import { StarIconButton } from '@atlaskit/rovo-agent-components/common/ui/StarIconButton';
import { isForgeAgentByCreatorType } from '@atlaskit/rovo-agent-components/common/utils/is-forge-agent';
import { AgentAvatar } from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import Tag from '@atlaskit/tag';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { type AIMateAgentAttributeType } from '@atlassian/conversation-assistant-instrumentation/types/analytics-properties-context';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import type { AgentRendererProps } from '../../../common/types.ts';
import { useAgentsPaginatedActionsWithOptimistic } from '../../../controllers/agents/index.tsx';
import { AgentCreatedBy } from '../../../utils/agents/index.tsx';
import { isForgeAgent, type CustomAgentCardMenuOption } from '../../types.ts';
import { mapAgentGqlToAgent } from '../../utils/agent-query-helper/index.ts';
import { useInViewport } from '../../utils/use-is-in-viewport/index.ts';
import { AgentDropdownMenu } from '../agent-interactions/index.tsx';

import type { agentCard_AtlassianConversationAssistantAgent_AgentCardContent_agentRef$key } from './__generated__/agentCard_AtlassianConversationAssistantAgent_AgentCardContent_agentRef.graphql.ts';
import type {
	agentCard_AtlassianConversationAssistantAgent_agentRef$data,
	agentCard_AtlassianConversationAssistantAgent_agentRef$key,
} from './__generated__/agentCard_AtlassianConversationAssistantAgent_agentRef.graphql.ts';
import messages from './messages.ts';

const styles = cssMap({
	avatarWrapper: {
		marginTop: token('space.050'),
		// The agent avatar's SVG masks intercept pointer events, preventing clicks from reaching the card's
		// pressable container. Disabling pointer events here lets clicks pass through to the card.
		pointerEvents: 'none',
	},

	cardContainer: {
		minHeight: '96px',
		listStyle: 'none',
		margin: 0,
		padding: 0,
	},

	cardContainerWithoutDescription: {
		minHeight: 'fit-content',
	},

	cardContent: {
		backgroundColor: token('elevation.surface'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		display: 'flex',
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		textAlign: 'left',
		position: 'relative',
		height: '100%',
		// this is to replicate <Box />, migrated to <div /> because `:hover backgroundColor`
		boxSizing: 'border-box',
	},

	toolbar: {
		listStyleType: 'none',
		padding: 0,
		margin: 0,
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		width: 'auto',
		justifyContent: 'space-evenly',
		position: 'relative',
		zIndex: 1,
	},

	toolbarListItem: {
		margin: 0,
		display: 'flex',
	},

	pressableAgentCard: {
		position: 'absolute',
		top: token('space.0'),
		left: token('space.0'),
		right: token('space.0'),
		bottom: token('space.0'),
		borderRadius: token('radius.large'),
		backgroundColor: 'transparent',
	},

	createdByLabel: {
		zIndex: 100,
		display: 'flex',
		alignItems: 'center',
		flexGrow: 1,
		backgroundColor: token('color.background.neutral.subtle'),
		padding: 0,
	},

	createdByText: {
		flexShrink: 0,
	},

	dropdownIconButtonWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		width: '24px',
	},

	hiddenIcon: {
		marginTop: token('space.025'),
	},

	hidden: {
		opacity: 0,
	},

	verifiedIconWrapper: {
		zIndex: 1,
	},
});

type AgentCardBaseProps = {
	agentRef: agentCard_AtlassianConversationAssistantAgent_agentRef$key;
	siteId: string;
	onViewAgent: () => void;
	isAutodevTemplate: boolean;
	agentCardRole?: 'link' | 'button';
	getAgentCardAriaLabel?: (agentName: string, agentJiraContext?: AgentRendererProps) => string;
	// onClickChat prop can be removed when all consumers are using onClickAgent
	onClickChat: () => void;
	onClickAgent?: () => void;
	onEditAgent: () => void;
	onCopyAgent: () => void;
	onDuplicateAgent: () => Promise<void>;
	onDeleteAgent: () => void;
	hideDescription?: boolean;
	userPermissionsRef?: AgentVerificationDropdownItemProps['userPermissionsRef'];
	onVerificationSuccess?: AgentVerificationDropdownItemProps['onVerificationSuccess'];
	customAgentDropdownMenuOptions?: CustomAgentCardMenuOption[];
	focusRef?: React.RefObject<HTMLButtonElement>;
} & AssistanceServiceInputParam;

export type AgentCardProps = AgentCardBaseProps & {
	renderFooter?: (props: AgentRendererProps) => ReactNode;
};

type AgentCardContentProps = Omit<AgentCardProps, 'agentRef'> & {
	agent: Agent;
	agentRef: agentCard_AtlassianConversationAssistantAgent_agentRef$data;
};

const AgentCardContent = ({
	agent,
	agentRef,
	siteId,
	onViewAgent,
	onEditAgent,
	onCopyAgent,
	onDuplicateAgent,
	onDeleteAgent,
	isAutodevTemplate,
	onClickChat,
	onClickAgent,
	assistanceServiceParams,
	agentCardRole,
	getAgentCardAriaLabel,
	hideDescription,
	userPermissionsRef,
	onVerificationSuccess,
	customAgentDropdownMenuOptions,
	renderFooter,
	focusRef,
}: AgentCardContentProps) => {
	di(AgentAvatar, useAgentsPaginatedActionsWithOptimistic);

	const [isFocusedOrHovered, setIsFocusedOrHovered] = useState(false);
	const [isAgentLinkCopied, setIsAgentLinkCopied] = useState(false);
	const { sendAnalyticsEvent } = useAnalytics();

	const setIsHoveredFalse = useCallback(() => {
		setIsFocusedOrHovered(false);
	}, []);

	const setIsHoveredTrue = useCallback(() => {
		setIsFocusedOrHovered(true);
		setIsAgentLinkCopied(false);
	}, []);

	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	const { formatMessage } = useIntl();

	const { setFavouriteAgent } = useAgentsPaginatedActionsWithOptimistic({
		assistanceServiceParams,
	});

	const agentCardData =
		useFragment<agentCard_AtlassianConversationAssistantAgent_AgentCardContent_agentRef$key>(
			graphql`
				fragment agentCard_AtlassianConversationAssistantAgent_AgentCardContent_agentRef on AgentStudioAssistant
				@argumentDefinitions(
					cloudId: { type: "ID!" }
					enableJiraAgentFields: { type: "Boolean!" }
				) {
					permissions @include(if: $enableJiraAgentFields) {
						canEdit
					}
					jiraAssignability(cloudId: $cloudId)
						@optIn(to: "JiraAssignableAgents")
						@include(if: $enableJiraAgentFields) {
						isAssignable
					}
				}
			`,
			agentRef,
		);

	const handleDropdownToggle = useCallback(
		(openMenuArgs: OnOpenChangeArgs) => setIsDropdownOpen(openMenuArgs.isOpen),
		[],
	);

	/**
	 * Keeping the `browseAgents<>` analytics here in case they're being used
	 * There should be equivalent analytics firing containing `source` which we should use instead
	 * TODO: Remove these analytics once we've migrated to the new analytics
	 */
	const handleViewAgent = useCallback(() => {
		onViewAgent();
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'browseAgentsViewAgentOption',
			attributes: {
				agentId: agent.id,
			},
		});
	}, [agent, onViewAgent, sendAnalyticsEvent]);

	const handleEdit = useCallback(() => {
		onEditAgent();
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'browseAgentsEditAgentOption',
			attributes: {
				agentId: agent.id,
			},
		});
	}, [agent.id, sendAnalyticsEvent, onEditAgent]);

	const handleCopy = useCallback(() => {
		onCopyAgent();
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'browseAgentsCopyAgentLinkOption',
			attributes: {
				agentId: agent.id,
			},
		});
	}, [agent.id, sendAnalyticsEvent, onCopyAgent]);

	const handleDelete = useCallback(() => {
		onDeleteAgent();

		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'browseAgentsDeleteAgentOption',
			attributes: {
				agentId: agent.id,
			},
		});
	}, [agent.id, sendAnalyticsEvent, onDeleteAgent]);

	const handleToggle = useCallback(async () => {
		await setFavouriteAgent(agent.id, !agent.favourite, agent.agentAri);

		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'browseAgentsFavoriteAgentButton',
			attributes: {
				interactionSource: 'browse-agents-content',
				isFavorited: !agent.favourite,
				agentId: agent.id,
			},
		});
	}, [setFavouriteAgent, agent.id, agent.favourite, agent.agentAri, sendAnalyticsEvent]);

	const handleCreatorLinkClick = useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			// Ensure the click doesn't trigger the card's click surface.
			e.stopPropagation();

			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'browseAgentsCreatorLink',
				attributes: {
					agentId: agent.id,
				},
			});
		},
		[agent.id, sendAnalyticsEvent],
	);

	const getAgentCreator = () => (
		<Text maxLines={1} color="color.text.subtle">
			<Inline grow="fill" alignBlock="center" space="space.050">
				{formatMessage(messages.createdByLabel, {
					text: (chunks: ReactNode[]) => <Box xcss={styles.createdByText}>{chunks}</Box>,
					creatorName: (
						<Text maxLines={1}>
							<AgentCreatedBy
								agent={agent}
								siteId={siteId}
								simplified
								onClick={handleCreatorLinkClick}
							/>
						</Text>
					),
				})}
			</Inline>
		</Text>
	);

	const getAgentInfoRender = () => {
		const nameAndCreatedByRender = (
			<Stack>
				<Inline space="space.075">
					<Box as="h3">
						<Text weight="bold" size="large" maxLines={2}>
							{agent.name}
						</Text>
					</Box>
					{fg('rovo_agents_agent_verification') && (
						<Box xcss={styles.verifiedIconWrapper}>
							<AgentVerified agentRef={agentRef} adjacentTextSize="textLarge" />
						</Box>
					)}
					{agent.visibility === 'PRIVATE' && (
						<Tooltip content={formatMessage(messages.hiddenTooltip)} position="top">
							<Box xcss={styles.hiddenIcon}>
								{/* TODO: replace with strikethrough eye icon once available:
								https://www.figma.com/design/z3sQjyYCCZja8dZo3w0NGq?node-id=9578-41906#837891177 */}
								<HiddenIcon label={formatMessage(messages.hiddenIcon)} />
							</Box>
						</Tooltip>
					)}
				</Inline>
				{!(
					agent.creator_type === 'REMOTE_A2A' &&
					FeatureGates.getExperimentValue(
						'rovo_hide_remote_a2a_agent_creator_exp',
						'isEnabled',
						false,
					)
				) && <Box xcss={styles.createdByLabel}>{getAgentCreator()}</Box>}
			</Stack>
		);

		return (
			<Inline space="space.150">
				<Box xcss={styles.avatarWrapper}>
					<AgentAvatar
						agentNamedId={agent.external_config_reference ?? agent.named_id}
						agentId={agent.id}
						agentIdentityAccountId={agent.identity_account_id}
						size="medium"
						showBorder={false}
						isForgeAgent={
							fg('rovo_agent_support_a2a_avatar')
								? isForgeAgentByCreatorType(agent.creator_type as AgentCreatorType)
								: isForgeAgent(agent)
						}
						forgeAgentIconUrl={agent.icon}
					/>
				</Box>
				{nameAndCreatedByRender}
			</Inline>
		);
	};

	const agentCardAriaLabel =
		getAgentCardAriaLabel?.(agent.name, {
			agentAri: agent.agentAri,
			creatorType: agent.creator_type,
			canEditAgent: agentCardData?.permissions?.canEdit,
			agentIsAssignableInJira: agentCardData?.jiraAssignability?.isAssignable,
		}) ??
		formatMessage(messages.chatWithAgent, {
			agentName: agent.name,
		});

	return (
		<Box
			xcss={styles.cardContent}
			onMouseEnter={setIsHoveredTrue}
			onMouseLeave={setIsHoveredFalse}
			onFocus={setIsHoveredTrue}
			onBlur={setIsHoveredFalse}
			testId="agent-card"
		>
			<Pressable
				ref={focusRef}
				xcss={styles.pressableAgentCard}
				onClick={onClickAgent ? onClickAgent : onClickChat}
				aria-label={agentCardAriaLabel}
				{...(agentCardRole === 'link' && {
					role: 'link',
					tabIndex: 0,
				})}
			/>
			<Stack grow="fill" space="space.100" spread="space-between">
				<Stack space="space.100">
					<Inline spread="space-between" space="space.100">
						{getAgentInfoRender()}
						{/* Wrapper div is required to prevent atlaskit/css-reset from adding a margin-top to the toolbar */}
						<div>
							<Box as="ul" xcss={styles.toolbar}>
								{isAutodevTemplate && (
									<Box as="li" xcss={styles.toolbarListItem} paddingInline="space.025">
										<Tag
											text={formatMessage(messages.templatesLozengeTitle)}
											color="gray"
											isRemovable={false}
											migration_fallback="lozenge"
										/>
									</Box>
								)}
								<Box
									as="li"
									testId="agent-card-dropdown-menu"
									xcss={cx(
										styles.dropdownIconButtonWrapper,
										styles.toolbarListItem,
										!isFocusedOrHovered && !isDropdownOpen && styles.hidden,
									)}
								>
									<AgentDropdownMenu
										spacing="compact"
										appearance="subtle"
										onDropdownTriggerClick={(_, e) => {
											sendAnalyticsEvent(
												e.update({
													actionSubjectId: 'browseAgentsOptionsButton',
													attributes: {
														agentId: agent.id,
													},
												}),
											);
										}}
										onOpenChange={handleDropdownToggle}
										isAutodevTemplateAgent={isAutodevTemplate}
										onEditAgent={handleEdit}
										onCopyAgent={handleCopy}
										onDuplicateAgent={onDuplicateAgent}
										onDeleteAgent={handleDelete}
										showViewAgentOption
										onViewAgentClick={handleViewAgent}
										isForgeAgent={
											fg('rovo_agent_support_a2a_avatar')
												? isForgeAgentByCreatorType(agent.creator_type as AgentCreatorType)
												: isForgeAgent(agent)
										}
										agentId={agent.id}
										agentName={agent.name}
										assistanceServiceParams={assistanceServiceParams}
										agentRef={agentRef}
										userPermissionsRef={userPermissionsRef ?? null}
										onVerificationSuccess={onVerificationSuccess}
										customDropdownOptions={customAgentDropdownMenuOptions?.map(
											(option: CustomAgentCardMenuOption) => ({
												...option,
												onClick: () => option.onClick(agent),
											}),
										)}
									/>
								</Box>
								<Box as="li" xcss={styles.toolbarListItem}>
									<Tooltip
										content={
											isAgentLinkCopied
												? formatMessage(messages.copyAgentLinkClickedTooltip)
												: formatMessage(messages.copyAgentLinkTooltip)
										}
									>
										<LinkIconButton
											handleCopy={() => {
												handleCopy();
												setIsAgentLinkCopied(true);
											}}
											visible={isFocusedOrHovered}
											agentName={agent.name}
										/>
									</Tooltip>
								</Box>
								<Box as="li" xcss={styles.toolbarListItem}>
									<StarIconButton
										isStarred={agent.favourite}
										handleToggle={handleToggle}
										visible
										agentName={agent.name}
									/>
								</Box>
							</Box>
						</div>
					</Inline>
					{!hideDescription && <Text maxLines={3}>{agent.description}</Text>}
				</Stack>
				{renderFooter &&
					renderFooter({
						agentAri: agent.agentAri,
						agentName: agent.name,
						creatorType: agent.creator_type,
						canEditAgent: agentCardData?.permissions?.canEdit,
						agentIsAssignableInJira: agentCardData?.jiraAssignability?.isAssignable,
					})}
				<VisuallyHidden role="status">
					{isAgentLinkCopied ? formatMessage(messages.copyAgentLinkClickedTooltip) : ''}
				</VisuallyHidden>
			</Stack>
		</Box>
	);
};

const getAgentAnalyticsAttributes = (agent: Agent) => {
	if (!agent) {
		return {};
	}

	if (fg('rovo_autodev_analytics_track_actions')) {
		const agentAnalyticsAttributes: AIMateAgentAttributeType = {
			agentId: agent.id,
			agentCreatorType: agent.creator_type ?? null,
			agentVisibility: agent.visibility ?? null,
			agentIsDefault: agent.is_default ?? null,
			agentExternalConfigReference: agent.external_config_reference ?? null,
			agentActions: agent.action_config?.map((config) => config.id) ?? [],
			agentIsVerified: agent.is_verified ?? null,
		};
		return agentAnalyticsAttributes;
	}

	return {
		agentId: agent.id,
	};
};

export const AgentCard = (props: AgentCardProps): React.JSX.Element => {
	di(useInViewport);

	/* eslint-disable @atlassian/relay/unused-fields */
	/* These fields are used in the mapAgentGqlToAgent function */
	const agentRef = useFragment(
		graphql`
			fragment agentCard_AtlassianConversationAssistantAgent_agentRef on AgentStudioAssistant
			@argumentDefinitions(enableJiraAgentFields: { type: "Boolean!" }, cloudId: { type: "ID!" }) {
				id
				name
				deactivated
				description
				isFavourite
				creatorType
				conversationStarters
				identityAccountId
				externalConfigReference
				creator {
					id
				}
				icon
				authoringTeam {
					id
					profileUrl
					displayName
					smallAvatarImageUrl
				}
				forgeCreator
				...agentVerified_AtlaskitRovoAgentComponents
				# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
				...agentVerificationDropdownItem_AtlaskitRovoAgentComponents_agentRef
				...agentCard_AtlassianConversationAssistantAgent_AgentCardContent_agentRef
					@arguments(enableJiraAgentFields: $enableJiraAgentFields, cloudId: $cloudId)
			}
		`,
		props.agentRef,
	);

	const agent = useMemo(() => mapAgentGqlToAgent(agentRef), [agentRef]);

	const containerRef = useRef(null);

	const { hasBeenInViewport } = useInViewport({
		ref: containerRef,
	});

	return (
		<AIMateAnalyticsContext
			data={{
				attributes: getAgentAnalyticsAttributes(agent),
			}}
		>
			<Box
				ref={containerRef}
				xcss={cx(
					styles.cardContainer,
					props.hideDescription && styles.cardContainerWithoutDescription,
				)}
				as="li"
			>
				{hasBeenInViewport && <AgentCardContent {...props} agent={agent} agentRef={agentRef} />}
			</Box>
		</AIMateAnalyticsContext>
	);
};
